import { useEffect, useRef } from "react";
import type { ChatMessage as ChatMessageType } from "../../../../shared/types";
import { ChatMessage } from "./ChatMessage.tsx";

interface ChatThreadProps {
  messages: ChatMessageType[];
  isWaiting?: boolean;
}

export function ChatThread({ messages, isWaiting = false }: ChatThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isWaiting]);

  if (messages.length === 0 && !isWaiting) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <p className="text-center text-sm text-slate-400">
          Start the brainstorm by sending a message!
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
      {messages.map((msg) => (
        <ChatMessage key={msg.id} message={msg} />
      ))}

      {/* Typing indicator when waiting for AI response */}
      {isWaiting && (
        <div className="flex justify-start">
          <div className="flex items-center gap-1 rounded-2xl bg-slate-100 px-4 py-3">
            <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:0ms]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:150ms]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:300ms]" />
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  );
}
